<script setup lang="ts">
/**
 * Address block aligned with the sales journey: a country select (full ISO list,
 * France pinned first) + an address line + a city/postcode field. For France the
 * city/postcode is a single autocomplete backed by geo.api.gouv.fr
 * (`useCommuneAutocomplete`); for other countries, two free inputs.
 *
 * Four v-models (line1, country, city, postcode). `validate()` is exposed for the
 * parent to run the final French commune check on submit.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  COUNTRY_CODES_ADDRESS,
  DEFAULT_COUNTRY,
  countryLabel,
  type AddressCountryCode,
} from '@/data/countries'
import type { Locale } from '@/i18n'
import {
  useCommuneAutocomplete,
  type CommuneSuggestion,
  type CommuneValidationError,
} from '@/composables/useCommuneAutocomplete'

const props = defineProps<{
  line1: string
  line2: string
  country: AddressCountryCode
  city: string
  postcode: string
}>()

const emit = defineEmits<{
  'update:line1': [v: string]
  'update:line2': [v: string]
  'update:country': [v: AddressCountryCode]
  'update:city': [v: string]
  'update:postcode': [v: string]
}>()

const { t, locale } = useI18n()

const line1Model = computed({ get: () => props.line1, set: (v) => emit('update:line1', v) })
const line2Model = computed({ get: () => props.line2, set: (v) => emit('update:line2', v) })
const countryModel = computed({ get: () => props.country, set: (v) => emit('update:country', v) })
const cityModel = computed({ get: () => props.city, set: (v) => emit('update:city', v) })
const postcodeModel = computed({ get: () => props.postcode, set: (v) => emit('update:postcode', v) })

/** Pays triés par label localisé, France épinglée en première position. */
const countryOptions = computed(() => {
  const collator = new Intl.Collator(locale.value, { sensitivity: 'base' })
  const labelled = COUNTRY_CODES_ADDRESS.map((code) => ({ code, label: countryLabel(code, locale.value as Locale) }))
  const fr = labelled.find((c) => c.code === DEFAULT_COUNTRY)
  const rest = labelled
    .filter((c) => c.code !== DEFAULT_COUNTRY)
    .sort((a, b) => collator.compare(a.label, b.label))
  return fr ? [fr, ...rest] : rest
})

const isCountryFrance = computed(() => props.country === 'FR')

/* --- Autocomplete commune française (ville/CP) dans un seul champ. La sélection
 *  est la source de vérité ; on miroir city/postcode vers le parent. --- */
const commune = useCommuneAutocomplete()
const cityPostcodeAsyncError = ref<string | null>(null)

const cityPostcodeModel = computed({
  get: () => commune.query.value,
  set: (v: string) => {
    commune.query.value = v
    commune.open.value = true
    cityPostcodeAsyncError.value = null
  },
})

const COMMUNE_ERROR_KEYS: Record<CommuneValidationError, string> = {
  'commune-not-found': 'communeNotFound',
  'postal-not-found': 'postalNotFound',
  'city-postal-mismatch': 'cityPostalMismatch',
}

watch(
  () => commune.selected.value,
  (sel) => {
    if (!isCountryFrance.value) return
    emit('update:city', sel?.city ?? '')
    emit('update:postcode', sel?.postalCode ?? '')
  },
)

function pickCommune(s: CommuneSuggestion) {
  commune.select(s)
  cityPostcodeAsyncError.value = null
}

/** Nom aléatoire : empêche le navigateur de proposer d'anciennes saisies libres
 *  (la valeur valide doit venir d'une sélection commune). */
const cityFieldName = `city-${Math.random().toString(36).slice(2, 10)}`

function onCountryChange() {
  emit('update:city', '')
  emit('update:postcode', '')
  commune.query.value = ''
  commune.reset()
  cityPostcodeAsyncError.value = null
}

// Pré-remplissage : si l'adresse existante est française, on réamorce la commune
// (libellé « CP Ville ») pour qu'elle soit traitée comme une sélection valide.
onMounted(() => {
  if (isCountryFrance.value && props.postcode && props.city) {
    commune.restore({
      label: `${props.postcode} ${props.city}`,
      postalCode: props.postcode,
      city: props.city,
      communeCode: '',
    })
  }
})

/** Validation finale (FR) : une commune sélectionnée est de confiance, sinon la
 *  saisie libre est vérifiée contre l'API. Renvoie true si valide (ou hors FR). */
async function validate(): Promise<boolean> {
  if (!isCountryFrance.value) return true
  const err = await commune.validate()
  if (err) {
    cityPostcodeAsyncError.value = t(`auth.account.fields.errors.${COMMUNE_ERROR_KEYS[err]}`)
    return false
  }
  emit('update:city', commune.selected.value?.city ?? '')
  emit('update:postcode', commune.selected.value?.postalCode ?? '')
  return true
}

defineExpose({ validate })
</script>

<template>
  <div class="address-fields">
    <!-- Pays -->
    <div class="field">
      <label class="field__label" for="addr-country">{{ t('auth.account.info.country') }}</label>
      <div class="jf-select">
        <select id="addr-country" v-model="countryModel" class="jf-select-el" @change="onCountryChange">
          <option v-for="opt in countryOptions" :key="opt.code" :value="opt.code">{{ opt.label }}</option>
        </select>
        <svg class="jf-select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
          <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>

    <!-- Adresse (numéro et voie) -->
    <div class="field">
      <label class="field__label" for="addr-line1">{{ t('auth.account.info.address') }}</label>
      <input
        id="addr-line1"
        v-model="line1Model"
        type="text"
        class="jf-input"
        :placeholder="t('auth.account.info.placeholders.line1')"
        autocomplete="street-address"
      />
    </div>

    <!-- Complément d'adresse (facultatif) -->
    <div class="field">
      <label class="field__label" for="addr-line2">
        {{ t('auth.account.info.addressComplement') }}
        <span class="field__optional">({{ t('auth.account.fields.optional') }})</span>
      </label>
      <input
        id="addr-line2"
        v-model="line2Model"
        type="text"
        class="jf-input"
        :placeholder="t('auth.account.info.placeholders.line2')"
        autocomplete="address-line2"
      />
    </div>

    <!-- Ville / CP : autocomplete commune (FR) sinon 2 champs libres. -->
    <div v-if="isCountryFrance" class="field address-fields__autocomplete">
      <label class="field__label" for="addr-city-postcode">{{ t('auth.account.fields.cityPostcode.labelFr') }}</label>
      <input
        id="addr-city-postcode"
        v-model="cityPostcodeModel"
        type="text"
        class="jf-input"
        :class="{ 'jf-input--invalid': cityPostcodeAsyncError }"
        :placeholder="t('auth.account.fields.cityPostcode.placeholderFr')"
        :name="cityFieldName"
        autocomplete="new-password"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        data-lpignore="true"
        data-1p-ignore="true"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="commune.open.value && (commune.suggestions.value.length > 0 || commune.loading.value)"
        aria-controls="addr-city-postcode-list"
        @focus="commune.onFocus()"
        @blur="commune.onBlur()"
      />
      <ul
        v-if="commune.open.value && commune.query.value.length >= 1"
        id="addr-city-postcode-list"
        class="address-fields__list"
        role="listbox"
      >
        <li
          v-if="commune.belowThreshold.value"
          class="address-fields__item address-fields__item--muted"
          aria-disabled="true"
        >{{ t('auth.account.fields.cityPostcode.minChars') }}</li>
        <li
          v-else-if="commune.loading.value"
          class="address-fields__item address-fields__item--muted"
          aria-disabled="true"
        >{{ t('auth.account.fields.cityPostcode.loading') }}</li>
        <li
          v-else-if="commune.suggestions.value.length === 0"
          class="address-fields__item address-fields__item--muted"
          aria-disabled="true"
        >{{ t('auth.account.fields.cityPostcode.empty') }}</li>
        <template v-else>
          <li
            v-for="s in commune.suggestions.value"
            :key="s.key"
            class="address-fields__item"
            role="option"
            @mousedown.prevent="pickCommune(s)"
          >
            <span class="address-fields__label">{{ s.label }}</span>
          </li>
        </template>
      </ul>
      <p v-if="cityPostcodeAsyncError" class="field__error">{{ cityPostcodeAsyncError }}</p>
    </div>

    <div v-else class="field">
      <span class="field__label">{{ t('auth.account.fields.cityPostcode.labelOther') }}</span>
      <div class="address-fields__city-row">
        <input
          v-model="cityModel"
          type="text"
          class="jf-input"
          :placeholder="t('auth.account.fields.cityPostcode.cityPlaceholder')"
          autocomplete="address-level2"
          :aria-label="t('auth.account.fields.cityPostcode.cityPlaceholder')"
        />
        <input
          v-model="postcodeModel"
          type="text"
          class="jf-input address-fields__postcode"
          :placeholder="t('auth.account.fields.cityPostcode.postcodePlaceholder')"
          autocomplete="postal-code"
          :aria-label="t('auth.account.fields.cityPostcode.postcodePlaceholder')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.address-fields { display: flex; flex-direction: column; gap: 1rem; }
.field { display: flex; flex-direction: column; gap: 0.4rem; }
.field__label { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.field__optional { margin-left: 0.3em; font-weight: 500; font-size: var(--text-xs); color: var(--text-muted); text-transform: lowercase; }
.field__error { font-size: var(--text-xs); font-weight: 600; color: var(--danger); }

.jf-select { position: relative; display: flex; align-items: center; }
.jf-select-el {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 0.7rem 2.5rem 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.jf-select-el:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }
.jf-select-chevron { position: absolute; right: 0.9rem; color: var(--text-muted); pointer-events: none; }

.jf-input {
  width: 100%;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.jf-input::placeholder { color: var(--text-muted); }
.jf-input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }
.jf-input--invalid { border-color: var(--danger); }

.address-fields__city-row {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.55fr);
}
@media (max-width: 26rem) {
  .address-fields__city-row { grid-template-columns: 1fr; }
}

.address-fields__autocomplete { position: relative; }
.address-fields__list {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  z-index: 5;
  max-height: 16rem;
  overflow-y: auto;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}
.address-fields__item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.55rem 0.85rem;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out);
}
.address-fields__item:hover { background: color-mix(in oklab, var(--color-peach) 18%, white); }
.address-fields__item--muted { color: var(--text-muted); font-style: italic; cursor: default; }
.address-fields__item--muted:hover { background: transparent; }
.address-fields__label { font-weight: 600; }
</style>
